import React, { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Accordion, Icon, TooltipPosition } from '@seeqdev/qomponents';
import { useFluxPath } from '@/core/hooks/useFluxPath.hook';
import { doTrack } from '@/track/track.service';
import { setAdvancedParametersCollapsedState } from '@/toolSelection/investigate.actions';
import _ from 'lodash';

export type ToolStore = {
  advancedParametersCollapsed: boolean;
};

interface AdvancedParametersProps {
  toolId: string;
  toolName: string;
  alternateName?: string;
  toolStore: ToolStore;
  children?: ReactNode;
  iconStyle?: string;
}

export const AdvancedParameters: React.FunctionComponent<AdvancedParametersProps> = (props) => {
  const { toolName, toolId, toolStore, alternateName = undefined, children, iconStyle } = props;
  const { t } = useTranslation();

  const collapsed = useFluxPath(toolStore, () => toolStore.advancedParametersCollapsed);

  const trackAndClick = () => {
    doTrack(toolName, 'Advanced Toggle', `Advanced ${collapsed ? 'expanded' : 'collapsed'}`);
    setAdvancedParametersCollapsedState(toolId, !collapsed);
  };

  return (
    <AdvancedSection
      collapsed={collapsed}
      alternateName={alternateName}
      trackAndClick={trackAndClick}
      t={t}
      iconStyle={iconStyle}>
      {children}
    </AdvancedSection>
  );
};

interface AdvancedSectionProps {
  collapsed: boolean;
  alternateName?: string;
  trackAndClick?: React.MouseEventHandler<HTMLDivElement>;
  children?: React.ReactNode;
  t: (key: string) => string;
  iconStyle?: string;
  tooltip?: string;
  tooltipDelay?: number;
  tooltipPlacement?: TooltipPosition;
}

export const AdvancedSection: React.FunctionComponent<AdvancedSectionProps> = ({
  collapsed,
  alternateName,
  trackAndClick,
  children,
  t,
  iconStyle,
  tooltip,
  tooltipDelay,
  tooltipPlacement,
}) => {
  const [activeAccordion, setActiveAccordion] = React.useState<string>(collapsed ? '' : '0');
  const determineIcon = (style: string | undefined) => {
    if (!_.isNil(style) && style === 'arrow') {
      if (activeAccordion !== '0') {
        return 'fa-angle-up';
      }
      return 'fa-angle-down';
    } else {
      if (activeAccordion !== '0') {
        return 'fa-plus';
      }
      return 'fa-minus';
    }
  };
  return (
    <div id="advancedParameters">
      <Accordion
        value={activeAccordion}
        onItemSelect={(key) => setActiveAccordion(key)}
        accordionItems={[
          {
            value: '0',
            id: '0',
            trigger: (
              <div
                data-testid="panelControl"
                className="cursorPointer pt5 pb5 sq-text-primary textAlignLeft"
                onClick={trackAndClick}>
                <Icon icon={determineIcon(iconStyle)} testId="advancedParametersIcon" />
                <a className="ml10 link-no-underline">{t(alternateName ? alternateName : 'ADVANCED')}</a>
                {tooltip && (
                  <Icon
                    icon="fa-question-circle"
                    large={true}
                    tooltip={t(tooltip)}
                    tooltipDelay={tooltipDelay}
                    tooltipPlacement={tooltipPlacement}
                    extraClassNames="pl5"
                  />
                )}
              </div>
            ),
            content: (
              <>
                <div className="divider" />
                {children}
              </>
            ),
          },
        ]}
      />
    </div>
  );
};
